import {
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { colors } from "../../../theme/palette";
import { Dispatch, SetStateAction } from "react";

const AudienceLocation = ({
  data,
  location,
  setLocation,
  isLoading,
}: {
  data: {
    location: string;
    countryName?: string;
    count: number;
  }[];
  location: "CITY" | "COUNTRY";
  setLocation: Dispatch<SetStateAction<"CITY" | "COUNTRY">>;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();

  const getEmptyMessage = () =>
    t("pages.dashboard.noDataAvailable", {
      context: location === "CITY" ? "cities" : "countries",
    });

  return (
    <Box
      sx={{
        width: "407px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "21px",
        }}
      >
        <Typography
          sx={{
            color: colors.Black100,
            fontFamily: "Inter",
            fontSize: "14px",
            fontWeight: 700,
          }}
        >
          {t("pages.dashboard.audienceLocation")}
        </Typography>

        <Box sx={{ display: "flex", columnGap: "16px" }}>
          <Chip
            label={t("pages.dashboard.cities")}
            component="button"
            variant="outlined"
            clickable
            onClick={() => setLocation("CITY")}
            sx={{
              border: "none",
              padding: "1px 4px",
              color: location === "CITY" ? colors.Green100 : colors.Dark600,
              background: location === "CITY" ? colors.Green25 : "transparent",
            }}
          />
          <Chip
            label={t("pages.dashboard.countries")}
            component="button"
            variant="outlined"
            clickable
            onClick={() => setLocation("COUNTRY")}
            sx={{
              border: "none",
              padding: "1px 4px",
              color: location === "COUNTRY" ? colors.Green100 : colors.Dark600,
              background:
                location === "COUNTRY" ? colors.Green25 : "transparent",
            }}
          />
        </Box>
      </Box>

      <TableContainer
        sx={{
          borderRadius: "16px",
          border: "1px solid #E7E7E7",
          background: "#FFF",
          padding: "17px 10px",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="left">
                {t("pages.dashboard.location")}
              </TableCell>
              <TableCell align="right">
                {t("pages.dashboard.popularity")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <LoadingTable />
            ) : data?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{ fontStyle: "italic", color: colors.Gray400 }}
                >
                  {t("pages.dashboard.noFollowersForTheseTypeOfLocations")}
                </TableCell>
              </TableRow>
            ) : (
              data?.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell component="th" scope="row" align="center">
                    {idx + 1}
                  </TableCell>
                  <TableCell align="left">
                    {location === "COUNTRY"
                      ? row?.location || ""
                      : `${row.location}, ${row.countryName}`}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: colors.tournamentColour,
                      fontFamily: "Inter",
                      fontSize: "13px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "normal",
                    }}
                  >
                    {row?.count || 0}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AudienceLocation;

// Skeleton Loading Component
const LoadingTable = () => {
  return (
    <>
      {Array.from({ length: 5 }).map((_, idx) => (
        <TableRow key={idx}>
          <TableCell align="center">
            <Skeleton variant="text" width={20} />
          </TableCell>
          <TableCell align="left">
            <Skeleton variant="text" width={150} />
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="text" width={50} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};
